'use client';

import { useEffect, useRef } from 'react';
import { useSettings, useUpdateSettings } from '@/hooks/useSettings';

// Mounts once inside the authenticated app shell. After the first time
// the settings query resolves, if the user's stored timezone is still
// the default `'UTC'` and the browser reports a different zone, we
// quietly PATCH the user's timezone so server-side day boundaries align
// with where the user actually is.
//
// Idempotent: the `triggered` ref means it only fires once per mount,
// and once the user's timezone is no longer UTC we stop trying. A user
// who genuinely wants UTC can set it manually in settings — we won't
// keep overwriting them because the auto-set runs at most once.
export function TimezoneAutoSync() {
  const settings = useSettings();
  const update = useUpdateSettings();
  const triggered = useRef(false);

  useEffect(() => {
    if (triggered.current) return;
    if (!settings.data) return;
    if (settings.data.timezone !== 'UTC') {
      triggered.current = true;
      return;
    }
    const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!browser || browser === 'UTC') {
      triggered.current = true;
      return;
    }
    triggered.current = true;
    update.mutate({ timezone: browser });
  }, [settings.data, update]);

  return null;
}
